import { useEffect, useState } from "react";
import { getCatalogLesson as getLesson } from "../../shared/catalog";
import { describeParams, timelineShape } from "../../shared/lesson-model";
import { api, ApiError } from "../lib/api";
import { Link } from "../lib/router";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import type { Adaptation } from "../lib/types";
import { SignInPanel } from "../components/SignInPanel";
import { StageStrip } from "../components/StageStrip";
import { Icon } from "../components/Icon";

type Tab = "adaptations" | "lessons" | "recent";

export default function Saved() {
  const { me, loading, favorites, toggleFavorite } = useSession();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("adaptations");
  const [saved, setSaved] = useState<Adaptation[] | null>(null);
  const [recent, setRecent] = useState<Adaptation[] | null>(null);

  useEffect(() => {
    if (!me?.user) return;
    api.adaptations("saved").then((r) => setSaved(r.adaptations)).catch((e: ApiError) => toast(e.message, "error"));
    api.adaptations("recent").then((r) => setRecent(r.adaptations)).catch(() => setRecent([]));
  }, [me?.user, toast]);

  if (loading) return <div className="page narrow"><p className="muted">Loading…</p></div>;
  if (!me?.user) {
    return (
      <div className="page narrow">
        <h1>Your shelf</h1>
        <p>Saved lessons and adaptations live here, on any device you sign in from.</p>
        <SignInPanel />
      </div>
    );
  }

  const remove = async (a: Adaptation) => {
    await api.deleteAdaptation(a.id);
    setSaved((xs) => xs?.filter((x) => x.id !== a.id) ?? null);
    toast("Deleted");
  };
  const keep = async (a: Adaptation) => {
    const r = await api.updateAdaptation(a.id, { saved: true, title: a.output.title });
    setRecent((xs) => xs?.filter((x) => x.id !== a.id) ?? null);
    setSaved((xs) => [r.adaptation, ...(xs ?? [])]);
    toast("Saved");
  };

  const favLessons = [...favorites].map((id) => getLesson(id)!).filter(Boolean);
  const renderList = (items: Adaptation[] | null, recentList = false) =>
    items === null ? <p className="muted">Loading…</p> : items.length === 0 ? (
      <div className="empty">
        <p>{recentList ? "Adaptations you haven't saved yet appear here for a while." : "Nothing saved yet. Adapt a lesson and press Save."}</p>
        <Link className="btn btn-primary" to="/">Find a lesson to adapt</Link>
      </div>
    ) : (
      <ul className="register">
        {items.map((a) => (
          <li className="row" key={a.id}>
            <Link to={`/a/${a.id}`} className="row-link">
              <span className="row-code">{a.lessonId}′</span>
              <span className="row-main">
                <span className="row-title">{a.title || a.output.title}</span>
                <span className="row-topic">{describeParams(a.params)}</span>
              </span>
              <span className="row-strip">
                <StageStrip stages={a.output.stages} size="xs" />
                <span className="small muted">{new Date(a.createdAt).toLocaleDateString()}</span>
              </span>
            </Link>
            {recentList ? (
              <button className="fav" onClick={() => void keep(a)} aria-label={`Save ${a.output.title}`}><Icon name="save" /></button>
            ) : (
              <button className="fav" onClick={() => void remove(a)} aria-label={`Delete ${a.output.title}`}><Icon name="trash" /></button>
            )}
          </li>
        ))}
      </ul>
    );

  return (
    <div className="page">
      <h1>Your shelf</h1>
      <div className="tabs" role="tablist">
        {([["adaptations", `Adaptations${saved ? ` (${saved.length})` : ""}`], ["lessons", `Saved lessons (${favLessons.length})`], ["recent", "Recent"]] as [Tab, string][]).map(([k, l]) => (
          <button role="tab" key={k} aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      {tab === "adaptations" && renderList(saved)}
      {tab === "recent" && renderList(recent, true)}
      {tab === "lessons" && (favLessons.length === 0 ? (
        <div className="empty"><p>Tap the heart on any lesson to keep it here.</p><Link className="btn btn-primary" to="/">Browse lessons</Link></div>
      ) : (
        <ul className="register">
          {favLessons.map((l) => (
            <li className="row" key={l.id}>
              <Link to={`/lesson/${l.id}`} className="row-link">
                <span className="row-code">{l.id}</span>
                <span className="row-main"><span className="row-title">{l.activity}</span><span className="row-topic">{l.topic}</span></span>
                <span className="row-strip"><StageStrip stages={timelineShape(l.duration)} size="xs" /><span className="small muted">{l.level}</span></span>
              </Link>
              <button className="fav on" onClick={() => void toggleFavorite(l.id)} aria-label={`Unsave ${l.id}`}><Icon name="heart" /></button>
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
}
